export const REWARD_TOTAL = 20000;
export const REWARD_PER_MILESTONE = 5000;
export const RESERVE_AMOUNT = 5000;
export const VALIDITY_HOURS = 48;

export const inr = (n: number) => "₹" + n.toLocaleString("en-IN");

/* ---------- Act 1 · the shift ---------- */

export const SHIFT_ROWS = [
  { what: "Matching 500 invoices", old: "6 hours", now: "4 minutes" },
  { what: "Audit testing", old: "5% sample", now: "100%" },
  { what: "Tax research and memos", old: "Days", now: "Minutes" },
  { what: "Month-end close", old: "1 AM", now: "6 PM" },
];

/* ---------- Act 2 · the market ---------- */

export const NEWS = {
  headline: "India's GCCs added 2.28 lakh jobs. Overall white-collar hiring fell 9%.",
  body: "The growth is in GCCs and MNC hubs. Two in three new roles now ask for AI, data or automation skills.",
  source: "Foundit Insights Tracker · Business Standard · BusinessToday",
  q: "So where are the accounting jobs opening in 2026?",
  opts: [
    "Traditional firms, same as always",
    "Nowhere, hiring is down",
    "GCCs and MNC hubs, if you have AI skills",
  ],
  answer: 2,
  teach:
    "The roles opening sit in India's GCCs, and 64% of them ask for AI skills. Fewer seats. A different accountant.",
};

export const INTERVIEW = {
  opts: [
    "How fast can you close the books?",
    "Which ERP do you know?",
    "How will you use AI?",
    "What is your expected salary?",
  ],
  answer: 2,
  teach:
    "They are not testing whether you use AI. They are testing your judgment about when.",
};

export const ANSWER_PIECES = [
  { k: "a", tag: "TASK", t: "For last month's ledger review…" },
  { k: "b", tag: "TOOL", t: "…I used Copilot in Excel to flag unusual entries…" },
  { k: "c", tag: "JUDGMENT", t: "…then checked every flag myself and fixed two errors…" },
  { k: "d", tag: "RESULT", t: "…which saved about three hours. Every month." },
];
export const ANSWER_SHUFFLE = ["c", "a", "d", "b"];

export const CVS = [
  { k: "a", t: "Only CPA / CMA", d: "Knows accounting · little AI" },
  { k: "b", t: "Only AI skills", d: "Knows the tools · no credential" },
  { k: "c", t: "CPA / CMA + AI skills", d: "Both, in one profile" },
];

/* ---------- Act 3 · CAIRA ---------- */

export const MYTHS = [
  {
    t: "AI will replace accountants.",
    truth: false,
    r: "It replaces the routine. Judgment, ethics and the client conversation stay yours, and get more valuable.",
  },
  {
    t: "You need to code to build AI agents.",
    truth: false,
    r: "Copilot Studio and Power Automate are no-code. You'll build a working agent in your first level.",
  },
  {
    t: "Firms are already paying for these tools.",
    truth: true,
    r: "Microsoft 365 Copilot, Copilot Studio, Power Automate and Power BI are already in most firms. Almost nobody uses them properly.",
  },
];

export const AGENT_TEAMS = {
  cfo: {
    n: "CFO teams",
    agents: [
      ["AP 3-Way Match Agent", "Matches invoice, PO and GRN · flags mismatches"],
      ["Variance Commentary Agent", "Turns P&L movements into board-ready narratives"],
      ["Rolling Forecast Agent", "Driver-based forecasts with scenario modelling"],
    ],
    run: [
      "AP 3-Way Match · 214 invoices matched · 2 exceptions flagged",
      "Variance Commentary · 6 movements explained · board pack drafted",
    ],
  },
  audit: {
    n: "Audit",
    agents: [
      ["Lease Audit Agent", "Extracts terms, tests classification, drafts the workpaper"],
      ["AR Confirmation Agent", "Sends confirmations, tracks responses, follows up"],
      ["PBC Tracker Agent", "Chases client documents, updates status dashboards"],
    ],
    run: [
      "AR Confirmation · 68 sent · 3 follow-ups queued",
      "Lease Audit · 41 leases tested · 2 classification exceptions",
    ],
  },
  tax: {
    n: "Tax",
    agents: [
      ["Tax Notice Copilot", "Drafts structured responses with cited provisions"],
      ["Provision Prep Agent", "Assembles the deferred tax working"],
      ["Research Digest Agent", "Summarises rulings into a one-page brief"],
    ],
    run: [
      "Tax Notice Copilot · draft response ready · 4 provisions cited",
      "Research Digest · 12 rulings summarised · 1 page",
    ],
  },
} as const;

export const LOGOS = [
  "Deloitte", "EY", "KPMG", "PwC", "BDO", "RSM", "Grant Thornton",
  "J.P. Morgan", "Morgan Stanley", "Genpact", "Amazon", "Accenture",
  "BlackRock", "Barclays", "+200 more",
];

export const MILES_GUESS = {
  q: "CAIRA comes from one institute. How many of India's CPAs came through it?",
  opts: ["Around 20%", "Around half", "80% or more"],
  answer: 2,
  facts: [
    ["80%", "of India's CPAs are Miles alumni"],
    ["70,000+", "alumni across 9 cities"],
    ["82% / 76%", "CPA and CMA pass rates vs ~47% and ~45% global"],
  ],
};

/* ---------- Act 4 · the AI check ---------- */
/*
 * Six games across three dimensions. Mindset 13 · Skillset 5 · Toolset 8 = 26 points,
 * shown as a percentage and as the three axes of the radar on the verdict card.
 */

export const DIMENSIONS = [
  { k: "mindset", label: "Mindset", max: 13, blurb: "Where you draw the line between the machine and you" },
  { k: "skillset", label: "Skillset", max: 5, blurb: "How well you brief the machine, and whether you check it" },
  { k: "toolset", label: "Toolset", max: 8, blurb: "Whether you can name the tool that owns each job" },
] as const;

export type DimensionKey = (typeof DIMENSIONS)[number]["k"];
export const CHECK_TOTAL = DIMENSIONS.reduce((n, d) => n + d.max, 0);

/* Which game feeds which dimension, in play order */
export const CHECK_GAMES = [
  { k: "swipe", dim: "mindset", max: 8, title: "Machine in your office?" },
  { k: "myths", dim: "mindset", max: 5, title: "Myth shredder" },
  { k: "excel", dim: "skillset", max: 4, title: "Talk to Excel" },
  { k: "trust", dim: "skillset", max: 1, title: "Trust or verify" },
  { k: "toolmap", dim: "toolset", max: 5, title: "Right tool, right job" },
  { k: "dash", dim: "toolset", max: 3, title: "Dashboard detective" },
] as const satisfies readonly { k: string; dim: DimensionKey; max: number; title: string }[];

/* Game 1 · Machine in your office? (8 swipes) */
export type SwipeCard = { t: string; machine: boolean; verdict: string };
export const SWIPE_CARDS: SwipeCard[] = [
  { t: "Pull data from 200 supplier invoices into a ledger", machine: true, verdict: "Power Automate reads all 200 and posts them while you sleep." },
  { t: "Decide whether a doubtful-debt provision is adequate", machine: false, verdict: "The provision call stays yours. That judgment is why you exist." },
  { t: "Draft a polite reminder chasing a confirmation", machine: true, verdict: "Copilot in Outlook drafts it in your tone. You hit send." },
  { t: "Spot duplicate vendor payments in a 5,000-row sheet", machine: true, verdict: "Copilot in Excel finds every duplicate before you finish reading row one." },
  { t: "Sign the audit opinion", machine: false, verdict: "Your licence, your name. No tool signs for you." },
  { t: "Summarise a 40-page lease into its key terms", machine: true, verdict: "Copilot in Word pulls the terms in a minute. You check them." },
  { t: "Judge whether a related-party deal is at arm's length", machine: false, verdict: "Arm's length is a judgment, not a lookup. Still a human call." },
  { t: "Match invoices to POs and flag mismatches", machine: true, verdict: "Power Automate matches the lot and flags what doesn't fit." },
];

/* Game 2 · Myth shredder (5 timed true/false) */
export type ShredCard = { t: string; truth: boolean; verdict: string };
export const SHRED_MYTHS: ShredCard[] = [
  { t: "Building an AI agent in Copilot Studio requires coding.", truth: false, verdict: "You describe the job in plain English. The studio builds the agent." },
  { t: "Excel can now read your data and answer questions you type in plain English.", truth: true, verdict: "That is Copilot in Excel." },
  { t: "Setting up an invoice approval flow needs your IT team.", truth: false, verdict: "Power Automate templates make it a form-fill, not a project." },
  { t: "The AI tools accountants can actually use at work live outside the software firms already run.", truth: false, verdict: "They sit inside Microsoft 365, which your firm most likely already pays for." },
  { t: "An AI agent can hand the tricky cases back to a human.", truth: true, verdict: "Escalation is a setting, not a limitation." },
];
export const SHRED_SECONDS = 10;
export const SHRED_CLOSE =
  "Everything in this round already sits inside the Microsoft 365 subscription most firms pay for. CAIRA Level 1 switches it on.";

/* Game 3 · Talk to Excel (pick 4 of 8 chips) */
export const BRIEF = {
  file: "AR_Aging.xlsx · 400 rows",
  rows: [
    ["Zenith Retail", "₹6,40,000", "112 days"],
    ["Orbit Foods", "₹2,10,000", "96 days"],
    ["Kite Textiles", "₹88,000", "34 days"],
  ],
  typed: "check this data",
  shrug: "Copilot shrugs. Too vague to act on.",
  chips: [
    { t: "Use the table AR_Aging", ok: true, why: "point at the data" },
    { t: "List invoices overdue past 90 days", ok: true, why: "name the ask" },
    { t: "Highlight the rows and summarise by client", ok: true, why: "shape the output" },
    { t: "Flag any client owing above ₹5 lakh", ok: true, why: "state the rule" },
    { t: "Be creative", ok: false },
    { t: "Please and thank you", ok: false },
    { t: "Make the answer longer", ok: false },
    { t: "Add emojis", ok: false },
  ],
  reveal:
    "Copilot is the sharpest junior you will ever brief: it does exactly what you say and nothing you did not. Point at the data, name the ask, shape the output, state the rule.",
};

/* Game 4 · Trust or verify (one planted contradiction) */
export const TRUST = {
  frame: "Copilot drafted your month-end commentary. The board pack leaves in 10 minutes. Tap anything wrong, or ship it.",
  table: [
    ["Revenue", "₹41.0L", "₹43.5L", "↑6%"],
    ["Travel", "₹4.2L", "₹4.7L", "↑12%"],
    ["Payroll", "₹12.1L", "₹12.1L", "flat"],
  ],
  draft: [
    { t: "Revenue rose 6 percent on two new retainers.", error: false },
    { t: "Travel spend fell 12 percent on tighter controls.", error: true },
    { t: "Payroll held flat month on month.", error: false },
  ],
  fine: "That line matches the table. Keep reading, or ship it.",
  caught: "That is the skill. Copilot drafts in seconds; you sign. Verification is the job now.",
  shipped:
    "The board just read that travel fell. The table beside it shows the rise. Copilot writes with total confidence either way; the signature is still yours.",
};

/* Game 5 · Right tool, right job (5 matches) */
export type ToolKey = "outlook" | "excel" | "automate" | "studio" | "powerbi";
export const TOOLS: { k: ToolKey; t: string; hint: string }[] = [
  { k: "outlook", t: "Copilot in Outlook", hint: "Copilot in Outlook writes and sends. This job isn't an email." },
  { k: "excel", t: "Copilot in Excel", hint: "Copilot in Excel explains the numbers in a sheet. Is this about a sheet?" },
  { k: "automate", t: "Power Automate", hint: "Power Automate runs a rule without you. Is this a rule?" },
  { k: "studio", t: "Copilot Studio", hint: "Copilot Studio builds an assistant that answers people. Is anyone asking?" },
  { k: "powerbi", t: "Power BI", hint: "Power BI shows the picture. Is this a picture?" },
];
export const TOOL_JOBS: { k: string; t: string; tool: ToolKey }[] = [
  { k: "j1", t: "Chase a confirmation politely, third reminder", tool: "outlook" },
  { k: "j2", t: "Explain why AR aging worsened this quarter", tool: "excel" },
  { k: "j3", t: "Route every invoice above ₹50,000 for approval", tool: "automate" },
  { k: "j4", t: "An assistant that answers “what is our travel policy?” all day", tool: "studio" },
  { k: "j5", t: "Which ten clients drive 80 percent of revenue, at a glance", tool: "powerbi" },
];
export const TOOLS_REVEAL =
  "Four names, one stack. Copilot works beside you. Power Automate works without you. Copilot Studio builds workers. Power BI keeps score.";

/* Game 6 · Dashboard detective (3 questions, tap a tile) */
export type TileKey = "revenue" | "ar" | "top10" | "cash";
export const DASH_QUESTIONS: { q: string; tile: TileKey; right: string }[] = [
  { q: "Who do we chase first for collections?", tile: "ar", right: "Right. The 90+ bucket is where collections start, and Power BI had it highlighted before you finished the question." },
  { q: "Can we make payroll if collections stall for 60 days?", tile: "cash", right: "74 days of runway says yes, with two weeks to spare." },
  { q: "Is the business growing or just busy?", tile: "revenue", right: "The trend tile answers in one glance: up nine of the last twelve months." },
];
export const DASH_WRONG = "That tile answers a different question.";
export const DASH_REVEAL =
  "You just read a live business in three taps. That is the whole job of Power BI: the answer arrives before the question is finished.";

/* Bands read off the percentage, so they hold whichever games are in play */
export const BANDS = [
  { min: 0, title: "AI Curious", verdict: "You know it's coming. You haven't touched it yet, and that's the gap firms screen for." },
  { min: 40, title: "AI Aware", verdict: "You get the idea. Now you need proof of work: agents you built, not tools you've heard of." },
  { min: 70, title: "AI Ready", verdict: "Sharp instincts. Turn them into a verifiable credential and you're the profile firms fight over." },
];

export const bandFor = (pct: number) =>
  BANDS.slice().reverse().find((b) => pct >= b.min) ?? BANDS[0];

/* ---------- pricing ---------- */

export const PLANS = {
  caira: {
    tab: "CAIRA",
    name: "CAIRA · all 3 levels",
    tag: "Certified AI-Ready Accountant · NASBA-approved CPE",
    fee: 89000,
    includes: [
      "3 levels · 90 hours · NASBA-approved CPE",
      "Miles AI Labs: build real AI agents, hands-on",
      "Masterclasses by global accounting innovators",
      "Level 1–3 verified badges for LinkedIn",
    ],
    nudge: "Adding US CPA or CMA? Same ₹5,000 reserve, same ₹20,000 off, and CAIRA drops to half price inside the bundle.",
  },
  cpa: {
    tab: "+ US CPA",
    name: "CAIRA + US CPA",
    tag: "4 exams · 12–16 months · Audit, Tax, CAS careers",
    fee: 149000,
    includes: [
      "Everything in CAIRA (all 3 levels)",
      "100+ hours of concept videos with Varun Jain",
      "McGraw Hill books · 8,000+ MCQs · 500+ simulations",
      "Placement support: Big 4 and 220+ MNCs",
    ],
    nudge: "The favourite hire: CPA + AI skills, one profile. This is that profile.",
  },
  cma: {
    tab: "+ US CMA",
    name: "CAIRA + US CMA",
    tag: "2 exams · 6–8 months · the CFO-track credential",
    fee: 149000,
    includes: [
      "Everything in CAIRA (all 3 levels)",
      "50+ hours of concept videos with Varun Jain",
      "McGraw Hill books · 5,000+ MCQs · 125+ case questions",
      "Placement support: Big 4 and 220+ MNCs",
    ],
    nudge: "The favourite hire: CMA + AI skills, one profile. This is that profile.",
  },
} as const;

export type PlanKey = keyof typeof PLANS;

/**
 * An extra reward on some tracks, shown as its own line so the ₹20,000 promised
 * across the whole flow still reads true. CMA earns more because the bundle
 * costs less to begin with — CMA is ₹10,000 below CPA in the catalog.
 */
export const BONUS_BY_TRACK: Partial<Record<PlanKey, number>> = {
  cma: 10000,
};

/**
 * What Miles 360's catalog charges for each track — the price the person is
 * actually billed after the reward comes off our list price. Mirrored here so a
 * test can prove the two never drift apart; the catalog itself is the authority,
 * and this app never edits it.
 */
export const CATALOG_PRICE: Record<PlanKey, number> = {
  caira: 69000,
  cpa: 129000,
  cma: 119000,
};

/** Where "Talk to a counsellor" goes: dials the Miles IVR. */
export const COUNSELLOR_URL = "tel:+919513881313";

export const FINE_PRINT =
  "Zero-interest 12-month EMI, no processing fee. The reserve amount is adjusted against your upfront payment; balance payable at enrolment. CPA: evaluation and exam fees payable to U.S. agencies are additional. CMA: IMA membership and exam fees additional. The ₹20,000 reward applies to the course fee and is locked by reserving within the validity window.";
